/**
 * The portal's only door to the server.
 *
 * THE ONE RULE THIS FILE EXISTS TO ENFORCE: a request that failed must never come back looking
 * like a successful request that found nothing. Every function returns
 * `{ ok: true, data }` or `{ ok: false, error, status }`, and no path anywhere converts a failure
 * into `[]`, `null`-as-data or an empty object. A client whose network dropped would otherwise be
 * told, in a beautifully designed empty state, that they have no dogs.
 *
 * Nothing here throws for an expected failure — offline, 401, 500, an HTML error page from a
 * reverse proxy, a truncated body. Callers branch on `ok`, never on try/catch.
 */

import {
  API_ROUTES,
  CUSTOMER_EDITABLE_MEAL_QIDS,
  parseDay,
  type CareItem,
  type EmergencyContactPayload,
  type FoodAvailability,
  type FoodEditRequest,
  type FoodEditResponse,
  type FoodEditValue,
  type FoodEditValuesResponse,
  type MarkGivenRequest,
  type MarkGivenResponse,
  type MePayload,
  type SessionInfo,
  type SignInResponse,
} from '@ft/shared'

// ── Result shape ────────────────────────────────────────────────────────────────────────────────

export interface ApiFailure {
  /**
   * Machine-readable. Either the server's own `error` code or one this module raises:
   * `network` (the request never completed), `timeout`, `aborted` (the CALLER cancelled — a
   * component unmounting, not a fault, so do not show it), `bad_response` (a 2xx whose body was
   * not the JSON shape the contract promises).
   */
  readonly code: string
  /** Safe to show a client. Never contains a stack, a URL or a header. */
  readonly message: string
  /** The server's correlation id, when it sent one. Worth putting in a support message. */
  readonly requestId?: string
}

export type ApiResult<T> =
  | { readonly ok: true; readonly data: T }
  /** `status` is the HTTP status, or 0 when the request never reached a server at all. */
  | { readonly ok: false; readonly error: ApiFailure; readonly status: number }

export interface RequestOptions {
  /** Abort when the caller goes away. The result then carries `code: 'aborted'` — ignore it. */
  readonly signal?: AbortSignal
  /** Defaults to 20s. A hung request is otherwise indistinguishable from a slow one, forever. */
  readonly timeoutMs?: number
}

const DEFAULT_TIMEOUT_MS = 20_000

/** Reasons a request can fail before any status code exists. */
const TRANSPORT_STATUS = 0

// ── Plumbing ────────────────────────────────────────────────────────────────────────────────────

function fail(status: number, code: string, message: string, requestId?: string): ApiResult<never> {
  // Built conditionally because `exactOptionalPropertyTypes` rejects an explicit `undefined`.
  const error: ApiFailure = requestId === undefined ? { code, message } : { code, message, requestId }
  return { ok: false, error, status }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Wording a client can act on. Deliberately vague about 4xx-vs-5xx detail: the code carries the
 * detail, the sentence carries the next step.
 */
function messageForStatus(status: number): string {
  if (status === 401 || status === 403) return 'Your sign-in has expired. Please sign in again.'
  if (status === 404) return 'We could not find that.'
  if (status === 429) return 'That was a lot of requests at once. Please wait a moment and try again.'
  if (status >= 500) return 'Something went wrong at our end. Please try again in a moment.'
  return 'We could not complete that request.'
}

function codeForStatus(status: number): string {
  if (status === 401) return 'unauthenticated'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status === 429) return 'rate_limited'
  return status >= 500 ? 'server_error' : 'request_failed'
}

/**
 * Combine the caller's signal with a timeout without `AbortSignal.any`, which needs Chrome 116 —
 * above the Chrome 111 floor the CSS already sets.
 */
function withDeadline(options: RequestOptions | undefined): {
  readonly signal: AbortSignal
  readonly done: () => void
  readonly timedOut: () => boolean
} {
  const controller = new AbortController()
  let expired = false

  const ms = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS
  const timer = setTimeout(() => {
    expired = true
    controller.abort()
  }, ms)

  const caller = options?.signal
  const onCallerAbort = (): void => controller.abort()
  if (caller) {
    if (caller.aborted) controller.abort()
    else caller.addEventListener('abort', onCallerAbort, { once: true })
  }

  return {
    signal: controller.signal,
    done: () => {
      clearTimeout(timer)
      caller?.removeEventListener('abort', onCallerAbort)
    },
    timedOut: () => expired,
  }
}

interface RequestSpec {
  readonly method: 'GET' | 'POST'
  readonly path: string
  readonly body?: unknown
}

/**
 * A guard's answer, boxed.
 *
 * `T | null` will not do: `signOut` legitimately succeeds with no payload, so `null` would have to
 * mean both "read it fine, there was nothing" and "could not read it" — and a 204 came back as
 * `bad_response`. A return type that means two things is the bug this whole file exists to avoid,
 * so the guard's own contract cannot have one either.
 */
type Parsed<T> = { readonly value: T } | null

/**
 * One request. Returns a result; the only way out is `ApiResult`.
 *
 * The body is read as text and parsed by hand rather than with `res.json()`, because a reverse
 * proxy, a captive portal or a dev server serving index.html on a proxy miss all answer 200 with
 * HTML. `res.json()` throws there; this reports `bad_response`, which is the truth.
 */
async function request<T>(
  spec: RequestSpec,
  parse: (payload: unknown) => Parsed<T>,
  options?: RequestOptions,
): Promise<ApiResult<T>> {
  const deadline = withDeadline(options)

  let response: Response
  try {
    response = await fetch(spec.path, {
      method: spec.method,
      // The session is an http-only cookie on the portal's own origin. Never 'include' — that
      // would offer the cookie cross-origin, and there is no cross-origin caller.
      credentials: 'same-origin',
      headers:
        spec.body === undefined
          ? { Accept: 'application/json' }
          : { Accept: 'application/json', 'Content-Type': 'application/json' },
      ...(spec.body === undefined ? {} : { body: JSON.stringify(spec.body) }),
      signal: deadline.signal,
    })
  } catch (cause) {
    // Every branch here has to disarm the deadline itself: the timer must survive past the headers
    // (see below), so it cannot be cleared in a `finally` that runs before the body is read.
    deadline.done()
    if (deadline.timedOut()) {
      return fail(TRANSPORT_STATUS, 'timeout', 'That took too long. Please check your connection and try again.')
    }
    if (options?.signal?.aborted) {
      // The caller walked away. Not a fault, and must not be rendered as one.
      return fail(TRANSPORT_STATUS, 'aborted', 'Request cancelled.')
    }
    void cause
    return fail(TRANSPORT_STATUS, 'network', 'We could not reach the portal. Please check your connection.')
  }

  // The deadline deliberately stays armed until the BODY is read, not just until the headers
  // arrive. A response that stalls mid-body is exactly as stuck as one that never answers, and
  // clearing the timer here would leave it hanging forever with the spinner still up.
  const text = await response.text().catch(() => '')
  deadline.done()

  let payload: unknown = null
  if (text.trim()) {
    try {
      payload = JSON.parse(text)
    } catch {
      payload = null
    }
  }

  if (!response.ok) {
    const body = isRecord(payload) ? payload : {}
    const code = typeof body['error'] === 'string' ? body['error'] : codeForStatus(response.status)
    const message = typeof body['message'] === 'string' ? body['message'] : messageForStatus(response.status)
    const requestId = typeof body['requestId'] === 'string' ? body['requestId'] : undefined
    return fail(response.status, code, message, requestId)
  }

  // A 2xx whose body is not the promised shape is a FAILURE, not an empty record. This is the
  // exact line that stops a proxy's HTML from rendering as "this client has no dogs".
  const parsed = parse(payload)
  if (parsed === null) {
    return fail(
      response.status,
      'bad_response',
      'The portal sent something we could not read. Please try again in a moment.',
    )
  }
  return { ok: true, data: parsed.value }
}

// ── Reviving the wire ───────────────────────────────────────────────────────────────────────────

/**
 * `CareItem.due` is a `Date` in the shared contract, and JSON has no Date — it arrives as a string.
 * Left alone, the first `fmtDay(item.due)` on a screen throws "toLocaleDateString is not a
 * function". Reviving it here, once, is what lets every screen treat `CareItem` as the type says.
 *
 * The date part is sliced off an ISO datetime BEFORE parsing rather than parsed as an instant:
 * a due date is a calendar day, and re-deriving that day from a UTC instant in the browser's own
 * timezone is precisely how a treatment ends up rendering a day early.
 */
function reviveDue(value: unknown): Date | null | undefined {
  // An absent key and an explicit null both mean the same thing the type does: no due date. A
  // value of the WRONG TYPE is different — it means the contract broke — and returns undefined,
  // which fails the payload rather than quietly becoming "no date set".
  if (value === null || value === undefined) return null
  if (typeof value !== 'string') return undefined
  const day = /^\d{4}-\d{2}-\d{2}/.exec(value)
  return parseDay(day ? day[0] : value)
}

export function reviveCareItems(value: unknown): CareItem[] | null {
  if (!Array.isArray(value)) return null
  const items: CareItem[] = []
  for (const raw of value) {
    if (!isRecord(raw)) return null
    const due = reviveDue(raw['due'])
    if (due === undefined) return null
    items.push({ ...(raw as unknown as CareItem), due })
  }
  return items
}

// ── Shape guards ────────────────────────────────────────────────────────────────────────────────
//
// Deliberately shallow: they check the properties whose absence would render as a plausible-looking
// lie (no dogs, no schedule, no name), not every leaf. A deep validator belongs here later — zod is
// already a server dependency — but a shallow guard that actually runs beats a perfect one that
// does not exist.

function asMePayload(payload: unknown): Parsed<MePayload> {
  if (!isRecord(payload)) return null
  if (typeof payload['customerId'] !== 'string') return null
  if (!isRecord(payload['owner'])) return null
  if (!Array.isArray(payload['dogs'])) return null

  const dogs: unknown[] = []
  for (const dog of payload['dogs']) {
    if (!isRecord(dog)) return null
    const care = reviveCareItems(dog['care'])
    if (care === null) return null
    dogs.push({ ...dog, care })
  }

  return { value: { ...payload, dogs } as unknown as MePayload }
}

function asSessionInfo(payload: unknown): Parsed<SessionInfo> {
  if (!isRecord(payload)) return null
  if (typeof payload['customerId'] !== 'string') return null
  if (typeof payload['expiresAt'] !== 'string') return null
  return { value: payload as unknown as SessionInfo }
}

function asSignInResponse(payload: unknown): Parsed<SignInResponse> {
  if (!isRecord(payload)) return null
  if (typeof payload['message'] !== 'string') return null
  return { value: payload as unknown as SignInResponse }
}

function asMarkGivenResponse(payload: unknown): Parsed<MarkGivenResponse> {
  if (!isRecord(payload)) return null
  const care = reviveCareItems(payload['care'])
  if (care === null) return null
  return { value: { ok: true, care } }
}

function asFoodAvailability(value: unknown): value is FoodAvailability {
  if (!isRecord(value) || typeof value['state'] !== 'string') return false
  if (value['state'] === 'missing' || value['state'] === 'unconfirmed') return true
  if (value['state'] !== 'available' || !isRecord(value['plan'])) return false
  if (typeof value['linkMethod'] !== 'string') return false
  const ownerEdited = value['ownerEdited']
  return ownerEdited === undefined || (isRecord(ownerEdited) && typeof ownerEdited['on'] === 'string')
}

/** Guard the food save response before it is allowed to replace a dog's rendered plan. */
export function asFoodEditResponse(payload: unknown): Parsed<FoodEditResponse> {
  if (!isRecord(payload) || payload['ok'] !== true || !asFoodAvailability(payload['food'])) return null
  return { value: payload as unknown as FoodEditResponse }
}

/** Every editable qid must be present and carry only the shared field editor's value shapes. */
export function asFoodEditValuesResponse(payload: unknown): Parsed<FoodEditValuesResponse> {
  if (!isRecord(payload) || !isRecord(payload['values'])) return null
  const values = payload['values']
  for (const qid of CUSTOMER_EDITABLE_MEAL_QIDS) {
    const value = values[qid]
    if (typeof value !== 'string' && !(Array.isArray(value) && value.every((item) => typeof item === 'string'))) {
      return null
    }
  }
  if (Object.keys(values).some((qid) => !CUSTOMER_EDITABLE_MEAL_QIDS.some((allowed) => allowed === qid))) {
    return null
  }
  return { value: { values: values as Readonly<Record<string, FoodEditValue>> } }
}

function asEmergencyContact(payload: unknown): Parsed<EmergencyContactPayload> {
  if (!isRecord(payload)) return null
  // Every field is legitimately null — this contact is a third party who filled in no form. What
  // must not pass is a non-object, which is why the guard checks the container and not the leaves.
  return { value: payload as unknown as EmergencyContactPayload }
}

/** For endpoints whose success genuinely carries no payload: a 204, or an acknowledging `{}`. */
function asNothing(): Parsed<null> {
  return { value: null }
}

// ── The seven calls ─────────────────────────────────────────────────────────────────────────────

/** Who, if anyone, is signed in. A 401 here is the normal signed-out answer, not an error to show. */
export function getSession(options?: RequestOptions): Promise<ApiResult<SessionInfo>> {
  return request({ method: 'GET', path: API_ROUTES.session }, asSessionInfo, options)
}

/**
 * Ask for a magic link.
 *
 * The response is identical whether or not the address matched a client — that is deliberate on the
 * server, and the UI must not undo it by rendering anything conditional on the answer.
 */
export function signIn(email: string, options?: RequestOptions): Promise<ApiResult<SignInResponse>> {
  return request({ method: 'POST', path: API_ROUTES.signIn, body: { email } }, asSignInResponse, options)
}

/** Exchange a magic-link token for a session cookie. */
export function consumeToken(token: string, options?: RequestOptions): Promise<ApiResult<SessionInfo>> {
  return request({ method: 'POST', path: API_ROUTES.consume, body: { token } }, asSessionInfo, options)
}

/** Ends the session. Resolves `data: null` — success here is the absence of an error. */
export function signOut(options?: RequestOptions): Promise<ApiResult<null>> {
  return request({ method: 'POST', path: API_ROUTES.signOut }, asNothing, options)
}

/**
 * The whole household in one call: owner, every dog, and each dog's care schedule and food plan
 * ALREADY DERIVED server-side. The client formats these; it never recomputes a due date, because
 * two implementations of the schedule would drift and the client's clock cannot be trusted.
 */
export function getMe(options?: RequestOptions): Promise<ApiResult<MePayload>> {
  return request({ method: 'GET', path: API_ROUTES.me }, asMePayload, options)
}

/**
 * The portal's ONLY write.
 *
 * `MarkGivenRequest.treatment` is typed to worming and flea alone. Booster and kennel cough are
 * vet-issued compliance artefacts the business's insurance and licence depend on — a one-tap
 * self-certification would create a record the business would then have to defend. They are not
 * representable in the request type; do not widen it.
 *
 * The response carries the recomputed schedule, so nothing on the client has to guess what moved.
 */
export function markGiven(
  body: MarkGivenRequest,
  options?: RequestOptions,
): Promise<ApiResult<MarkGivenResponse>> {
  return request({ method: 'POST', path: API_ROUTES.markGiven, body }, asMarkGivenResponse, options)
}

/** Load the identity-safe, pending-write-overlaid values for one owner's confirmed meal record. */
export function getFoodEditValues(
  dogId: string,
  options?: RequestOptions,
): Promise<ApiResult<FoodEditValuesResponse>> {
  return request(
    { method: 'GET', path: API_ROUTES.foodEditValues(dogId) },
    asFoodEditValuesResponse,
    options,
  )
}

/** Queue an owner food edit; the response already contains the immediately effective food plan. */
export function editFood(
  body: FoodEditRequest,
  options?: RequestOptions,
): Promise<ApiResult<FoodEditResponse>> {
  return request({ method: 'POST', path: API_ROUTES.foodEdit, body }, asFoodEditResponse, options)
}

/**
 * The emergency contact, behind a deliberate request.
 *
 * These ~156 people never filled in a form and never saw a privacy notice, so this is a separate
 * endpoint and every reveal is audited server-side. Never call it to pre-populate a screen — only
 * in response to the client actually asking to see it.
 */
export function getEmergencyContact(
  dogId: string,
  options?: RequestOptions,
): Promise<ApiResult<EmergencyContactPayload>> {
  return request({ method: 'GET', path: API_ROUTES.emergencyContact(dogId) }, asEmergencyContact, options)
}
